import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { Bill } from "@common/types/bill";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { toScheduleCardValues } from "@components/invoices/views/scheduleCellState";
import { BillScheduleCellContent } from "@components/bills/schedules/BillScheduleCellContent";
import { BillScheduleTableOptions } from "@components/bills/schedules/useBillScheduleTableOptions";
import { useBillRowSchedule } from "@components/bills/schedules/useBillRowSchedule";
import { BillsViewCallbacks } from "../types";

export const ScheduleCell = memo(
    ({ row, table }: CellContext<ViewTableFeatures, Bill, unknown>) => {
        const { callbacks, options } = useViewTableMeta<
            BillsViewCallbacks,
            BillScheduleTableOptions
        >(table);
        const bill = row.original;
        const schedule = useBillRowSchedule({ bill, callbacks });

        const scheduleTemplates = useMemo(
            () =>
                resolvePickerTemplates({
                    templates: options?.scheduleTemplates,
                    entityId: bill.entityId,
                    prioritizedTemplateId:
                        options?.prioritizedScheduleTemplateId,
                }),
            [
                options?.scheduleTemplates,
                options?.prioritizedScheduleTemplateId,
                bill.entityId,
            ],
        );

        if (schedule.kind === "static") {
            return (
                <ViewTableCell>
                    <BillScheduleCellContent state={schedule.state} />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <SchedulePicker
                    templates={scheduleTemplates}
                    applied={toScheduleCardValues(schedule.state)}
                    prioritizedTemplateId={
                        options?.prioritizedScheduleTemplateId
                    }
                    {...schedule.pickerProps}
                />
                {schedule.overlays}
            </ViewTableCell>
        );
    },
);

ScheduleCell.displayName = "ScheduleCell";
